import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { portalApi, tournamentApi } from '../../services/api'
import PortalShell from './PortalShell'
import ProviderCredentialsForm from '../../components/ProviderCredentialsForm'
import { isOnboardingComplete } from './onboardingStatus'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Tee Sheet Provider picker.
 * InteGolf is the only functional option today. Others are shown but
 * marked "Coming Soon" until their adapters are ported in Phase 12.
 *
 * PROVIDERS is built as a function so taglines/descriptions/feature chips
 * can be translated. Provider proper names (Tee-On, ChronoGolf, TTC, etc.)
 * stay literal since they are brand names.
 */
function buildProviders(t) {
  return [
    {
      key: 'integolf',
      name: t('sp_provider_generic_teesheet', 'Generic TeeSheet'),
      tagline: t('sp_provider_integolf_tag', 'Use our built-in tee sheet (recommended)'),
      description: t('sp_provider_integolf_desc', 'Manage your tee times, pricing, and bookings directly in InteGolf. Full control with real-time availability for your Stay & Play packages.'),
      icon: '⛳',
      features: [t('sp_feature_full_control', 'Full control'), t('sp_feature_real_time_sync', 'Real-time sync'), t('sp_feature_no_third_party', 'No third-party API needed'), t('sp_feature_included_free', 'Included free')],
      available: true,
    },
    {
      key: 'teeon-classic',
      name: 'Tee-On (Classic)',
      tagline: t('sp_provider_teeon_classic_tag', 'Connect your existing Tee-On account'),
      description: t('sp_provider_teeon_classic_desc', 'Bookings sync directly with your Tee-On tee sheet. Requires ApplicationKey + AccessToken from Tee-On.'),
      icon: '🏌',
      features: [t('sp_feature_keep_setup', 'Keep existing setup'), t('sp_feature_sync_both_ways', 'Sync bookings both ways')],
      available: true,
    },
    {
      key: 'teeon-gn',
      name: 'Tee-On (Golf North)',
      tagline: t('sp_provider_teeon_gn_tag', 'Modern Tee-On API for Golf North courses'),
      description: t('sp_provider_teeon_gn_desc', 'For courses using the newer Golf North Tee-On platform. Requires a Facility-Token from Tee-On.'),
      icon: '🏌',
      features: [t('sp_feature_modern_rest', 'Modern REST API'), t('sp_feature_realtime_pricing', 'Real-time pricing')],
      available: true,
    },
    {
      key: 'chronogolf',
      name: 'ChronoGolf',
      tagline: t('sp_provider_chronogolf_tag', 'Lightspeed Golf / ChronoGolf integration'),
      description: t('sp_provider_chronogolf_desc', 'Connect your ChronoGolf account via the Lightspeed Golf Partner API. Pick your organization from the live catalog and a player type to book under.'),
      icon: '⏱',
      features: [t('sp_feature_live_org_picker', 'Live organization picker'), t('sp_feature_player_type_map', 'Player-type mapping'), t('sp_feature_auto_oauth', 'Auto-refresh OAuth')],
      available: true,
    },
    {
      key: 'ttc',
      name: 'TeeTimeCentral (TTC)',
      tagline: t('sp_provider_ttc_tag', 'TTC or ChronoGolf via InteGolf partner account'),
      description: t('sp_provider_ttc_desc', 'Shared InteGolf partner account. Pick your course from the live TTC catalog — no credentials to copy. Contact TTC to get added to our partner account.'),
      icon: '📅',
      features: [t('sp_feature_no_credentials', 'No credentials'), t('sp_feature_course_rate_picker', 'Live course + rate picker'), t('sp_feature_covers_chronogolf', 'Covers ChronoGolf')],
      available: true,
    },
    {
      key: 'club-prophet',
      name: 'Club Prophet',
      tagline: t('sp_provider_club_prophet_tag', 'Club Prophet tee sheet integration'),
      description: t('sp_provider_club_prophet_desc', 'Integration planned. Club Prophet\'s API is partner-only — request access at clubprophet.com/contact/integration-request.'),
      icon: '💰',
      features: [t('sp_feature_planned_2026', 'Planned for 2026')],
      available: false,
      comingSoon: true,
    },
    {
      key: 'teetime-com',
      name: 'tee-time.com (Libeo)',
      tagline: t('sp_provider_teetimecom_tag', 'Search via the Libeo / VIPGolf marketplace'),
      description: t('sp_provider_teetimecom_desc', 'Pull live tee times from tee-time.com. Optionally filter to a specific organization. Search-only for now — booking requires VIPGolf partner credentials we are working on.'),
      icon: '🌐',
      features: [t('sp_feature_search_market', 'Search marketplace'), t('sp_feature_geo_aware', 'Geo-aware'), t('sp_feature_read_only', 'Read-only')],
      available: true,
    },
  ]
}

export default function TeeSheetSetup() {
  const { t } = useLanguage()
  const PROVIDERS = buildProviders(t)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [currentProvider, setCurrentProvider] = useState(null)
  const [selectedKey, setSelectedKey] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [credentials, setCredentials] = useState({})
  const [testResult, setTestResult] = useState(null)
  const [status, setStatus] = useState(null)
  const [packages, setPackages] = useState([])

  // Providers that require credentials before saving — InteGolf is our own,
  // so no credentials needed there. The form component handles "no fields"
  // by rendering null for integolf.
  const requiresCredentials = selectedKey && selectedKey !== 'integolf'

  // Load the current provider + any previously saved credentials + onboarding status
  useEffect(() => {
    tournamentApi.get('/play-stay/packages')
      .then(({ data }) => setPackages(Array.isArray(data) ? data : []))
      .catch(() => setPackages([]))
    portalApi.get('/onboarding/status')
      .then(({ data }) => {
        setStatus(data)
        const p = data.settings?.provider
        if (p) {
          setCurrentProvider(p)
          setSelectedKey(p)
          // Hydrate saved creds so admins can edit without re-entering everything
          if (data.settings?.credentials) {
            setCredentials(data.settings.credentials)
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // Reset credential state when switching providers
  function handleSelectProvider(key) {
    if (key === selectedKey) return
    setSelectedKey(key)
    // If switching back to the currently-saved provider, restore its creds;
    // otherwise start fresh.
    setCredentials({})
    setTestResult(null)
    setError('')
  }

  async function handleConfirm() {
    if (!selectedKey) return
    const provider = PROVIDERS.find(p => p.key === selectedKey)
    if (!provider?.available) {
      return setError(t('sp_provider_coming_soon_warning', 'This provider is coming soon. For now, please choose InteGolf\'s Tee Sheet.'))
    }
    // If the provider requires credentials, require a successful test first.
    // This prevents merchants from saving bad credentials and hitting errors
    // at first real booking.
    if (requiresCredentials && (!testResult || !testResult.ok)) {
      return setError(t('sp_test_connection_required', 'Please run "Test connection" successfully before saving.'))
    }
    setError('')
    setSubmitting(true)
    try {
      await portalApi.post('/onboarding/provider', {
        provider: selectedKey,
        credentials: requiresCredentials ? credentials : null,
      })
      navigate('/portal')
    } catch (err) {
      setError(err.response?.data?.message || t('sp_provider_save_failed', 'Failed to save provider'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <PortalShell>
      <div style={{ maxWidth: 1000, margin: '40px auto', padding: '0 20px' }}>
        <div style={{ marginBottom: 28 }}>
          {!isOnboardingComplete(status, packages) && (
            <div style={{ fontSize: 11, letterSpacing: 2, color: 'var(--brand-600)', fontWeight: 700, marginBottom: 6 }}>
              {t('sp_setup_step_2_teesheet_caps', 'SETUP · STEP 2 OF 4 · TEE SHEET')}
            </div>
          )}
          <h1 style={{ margin: 0, fontSize: 28, color: 'var(--secondary-950)' }}>
            {t('sp_connect_tee_sheet', 'Connect your tee sheet')}
          </h1>
          <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 15 }}>
            {t('sp_teesheet_blurb', 'Choose which tee sheet provider you want to use for Stay & Play bookings. We\'ll pull available tee times and process bookings through it.')}
          </p>
        </div>

        {loading ? (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            {t('loading_ellipsis', 'Loading...')}
          </div>
        ) : (
          <>
            {error && (
              <div style={{
                padding: '10px 14px', marginBottom: 16,
                background: '#fef2f2', color: '#dc2626',
                border: '1px solid #fecaca', borderRadius: 8, fontSize: 13,
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16, marginBottom: 24 }}>
              {PROVIDERS.map(p => (
                <ProviderCard
                  key={p.key}
                  provider={p}
                  selected={selectedKey === p.key}
                  current={currentProvider === p.key}
                  onSelect={() => p.available && handleSelectProvider(p.key)}
                />
              ))}
            </div>

            {/* Provider-specific credential form. Renders null for integolf. */}
            {selectedKey && PROVIDERS.find(p => p.key === selectedKey)?.available && (
              <ProviderCredentialsForm
                provider={selectedKey}
                initialCredentials={credentials}
                onChange={setCredentials}
                onTestResult={setTestResult}
              />
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <button
                type="button"
                onClick={() => navigate('/portal')}
                className="btn btn-outline-dark"
                style={{ padding: '12px 24px' }}
              >
                {t('cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={
                  submitting
                  || !selectedKey
                  || !PROVIDERS.find(p => p.key === selectedKey)?.available
                  || (requiresCredentials && (!testResult || !testResult.ok))
                }
                className="btn btn-primary"
                style={{
                  padding: '14px 32px',
                  opacity: (
                    submitting
                    || !selectedKey
                    || !PROVIDERS.find(p => p.key === selectedKey)?.available
                    || (requiresCredentials && (!testResult || !testResult.ok))
                  ) ? 0.5 : 1,
                }}
              >
                {submitting ? t('sp_saving', 'Saving...') : (currentProvider === selectedKey ? t('sp_save_changes', 'Save changes') : t('sp_use_this_provider', 'Use this provider'))}
              </button>
            </div>
          </>
        )}
      </div>
    </PortalShell>
  )
}

function ProviderCard({ provider, selected, current, onSelect }) {
  const { t } = useLanguage()
  const isAvailable = provider.available
  return (
    <div
      onClick={onSelect}
      style={{
        position: 'relative',
        padding: 20,
        background: '#fff',
        border: '2px solid ' + (selected ? 'var(--brand-500)' : 'var(--card-border)'),
        borderRadius: 12,
        cursor: isAvailable ? 'pointer' : 'not-allowed',
        opacity: isAvailable ? 1 : 0.65,
        transition: 'all 0.15s ease',
        boxShadow: selected ? '0 4px 16px rgba(0, 209, 159, 0.15)' : 'none',
      }}
    >
      {/* ACTIVE badge only shows when the card is NOT selected (otherwise it collides with the ✓ checkmark in the top-right corner) */}
      {current && !selected && (
        <div style={currentBadge}>{t('sp_chip_active', 'ACTIVE')}</div>
      )}
      {!isAvailable && (
        <div style={comingSoonBadge}>{t('sp_coming_soon_caps', 'COMING SOON')}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 32, lineHeight: 1 }}>{provider.icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--secondary-950)' }}>{provider.name}</div>
          <div style={{ fontSize: 12, color: 'var(--brand-600)', fontWeight: 500 }}>{provider.tagline}</div>
        </div>
      </div>

      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        {provider.description}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {provider.features.map(f => (
          <span key={f} style={featureChipStyle}>{f}</span>
        ))}
      </div>

      {selected && isAvailable && (
        <div style={{
          position: 'absolute', top: 12, right: 12,
          width: 24, height: 24, borderRadius: '50%',
          background: 'var(--brand-500)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 14, fontWeight: 700,
        }}>
          ✓
        </div>
      )}
    </div>
  )
}

const currentBadge = {
  position: 'absolute',
  top: 12,
  right: 12,
  padding: '3px 10px',
  background: 'var(--brand-500)',
  color: '#fff',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1,
  borderRadius: 4,
}

const comingSoonBadge = {
  position: 'absolute',
  top: 12,
  right: 12,
  padding: '3px 10px',
  background: '#fef3c7',
  color: '#92400e',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1,
  borderRadius: 4,
  border: '1px solid #fde68a',
}

const featureChipStyle = {
  fontSize: 11,
  padding: '3px 10px',
  background: 'var(--brand-50)',
  color: 'var(--brand-700)',
  borderRadius: 12,
  border: '1px solid var(--brand-100)',
  fontWeight: 500,
}
